import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { TranscriptUtterance, EditorWord } from '../../types';
import { reconcileEditedText } from '../../utils/textDiffing';

/**
 * Extracts plain text from a contentEditable element that contains word spans
 */
const extractTextFromElement = (element: HTMLElement): string => {
  // Use textContent to get all text, including text nodes between spans
  // This ensures we capture text typed between existing spans
  const fullText = element.textContent || '';

  // Normalize whitespace - collapse multiple spaces to single space
  // and trim leading/trailing whitespace
  return fullText.replace(/\s+/g, ' ').trim();
};

/**
 * Cycles through word confidence states based on original confidence score
 *
 * Logic:
 * - If original score >= 0.8 (verified): Short cycle: Verified ↔ Inaudible
 * - If original score < 0.8 (low-confidence): Full cycle: Low-confidence → Verified → Inaudible → Low-confidence
 */
const cycleWordConfidence = (word: EditorWord): EditorWord => {
  const currentScore = word.confidenceScore;
  const originalScore = word.originalConfidenceScore ?? word.confidenceScore;

  let newScore: number;
  let newOriginalScore = originalScore;

  // If we haven't stored the original score yet, store it now
  if (word.originalConfidenceScore === undefined) {
    newOriginalScore = currentScore;
  }

  // Determine cycling behavior based on original confidence score
  if (originalScore >= 0.8) {
    // Short cycle: Verified ↔ Inaudible
    if (currentScore === 0) {
      // Inaudible → Verified (restore original)
      newScore = originalScore;
    } else {
      // Verified → Inaudible
      newScore = 0;
    }
  } else {
    // Full cycle: Low-confidence → Verified → Inaudible → Low-confidence
    if (currentScore === 0) {
      // Inaudible → Low-confidence (restore original)
      newScore = originalScore;
    } else if (currentScore === 1) {
      // Verified → Inaudible
      newScore = 0;
    } else {
      // Low-confidence → Verified
      newScore = 1;
    }
  }

  return {
    ...word,
    confidenceScore: newScore,
    originalConfidenceScore: newOriginalScore,
    isModified: true,
    lastModifiedAt: Date.now(),
  };
};

// Types for callbacks that the handlers will use
export interface InputHandlerCallbacks {
  onUtteranceSplit?: (
    utteranceId: string,
    beforeWords: EditorWord[],
    newUtteranceId: string,
    afterWords: EditorWord[]
  ) => void;
  onUtteranceMerge?: (utteranceId: string, previousUtteranceId: string) => void;
  onWordsEdit?: (utteranceId: string, newWords: EditorWord[]) => void;
}

/**
 * Handles general text input/editing in an utterance
 */
export const handleInput = (
  e: React.FormEvent<HTMLDivElement>,
  utteranceId: string,
  utteranceDataMap: Map<string, { utteranceIndex: number; startTime: number }>,
  utterances: TranscriptUtterance[],
  callbacks: InputHandlerCallbacks
) => {
  // Extract text content directly without normalizing
  // The component's ref callback will handle proper HTML structure with classes
  const newText = extractTextFromElement(e.currentTarget);

  // Find the utterance
  const utteranceData = utteranceDataMap.get(utteranceId);
  if (!utteranceData) return;

  const utterance = utterances[utteranceData.utteranceIndex];
  if (!utterance) return;

  // Generate final word array with preserved IDs
  const { newEditorWords, hasChanges } = reconcileEditedText(
    utterance.transcriptWords,
    newText,
    utterance.startTime,
    utterance.endTime
  );

  if (!hasChanges) {
    return;
  }

  // Notify parent component about the word changes
  if (callbacks.onWordsEdit) {
    callbacks.onWordsEdit(utteranceId, newEditorWords);
  }
};

/**
 * Handles paste events to ensure only plain text is pasted
 */
export const handlePaste = (e: React.ClipboardEvent) => {
  e.preventDefault();
  const text = e.clipboardData.getData('text/plain');
  const selection = window.getSelection();
  if (selection?.rangeCount) {
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);

    // Trigger input event to handle the change
    // The component's ref callback will handle proper HTML structure with classes
    const element = e.currentTarget as HTMLElement;
    setTimeout(() => {
      const inputEvent = new Event('input', { bubbles: true });
      element.dispatchEvent(inputEvent);
    }, 0);
  }
};

/**
 * Handles Enter key to split utterances
 */
const handleEnterKey = (
  e: React.KeyboardEvent,
  utteranceId: string,
  utteranceDataMap: Map<string, { utteranceIndex: number; startTime: number }>,
  utterances: TranscriptUtterance[],
  setUtterances: React.Dispatch<React.SetStateAction<TranscriptUtterance[]>>,
  callbacks: InputHandlerCallbacks
) => {
  e.preventDefault();

  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return;

  const range = selection.getRangeAt(0);
  const element =
    range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : (range.startContainer as HTMLElement);

  const utteranceElement = element?.closest(
    '[data-utterance-id] .utterance-content'
  ) as HTMLElement;
  if (!utteranceElement) return;

  const currentText = extractTextFromElement(utteranceElement);

  // Calculate absolute cursor position within the utterance
  let cursorPosition = 0;
  const walker = document.createTreeWalker(
    utteranceElement,
    NodeFilter.SHOW_TEXT,
    null
  );

  let textNode = walker.nextNode();
  while (textNode) {
    if (textNode === range.startContainer) {
      cursorPosition += range.startOffset;
      break;
    }
    cursorPosition += textNode.textContent?.length || 0;
    textNode = walker.nextNode();
  }

  const beforeText = currentText.substring(0, cursorPosition);
  const afterText = currentText.substring(cursorPosition);

  // First part always keeps the original ID, second part always gets new ID
  const firstPartId = utteranceId;
  const secondPartId = uuidv4();

  // Find the current utterance
  const utteranceData = utteranceDataMap.get(utteranceId);
  if (!utteranceData) return;

  const currentUtterance = utterances[utteranceData.utteranceIndex];
  if (!currentUtterance) return;

  // Step 1: First reconcile the entire current text to handle any word modifications
  // Add space where the split occurs to represent the change
  const fullText = beforeText + ' ' + afterText;
  const { newEditorWords } = reconcileEditedText(
    currentUtterance.transcriptWords,
    fullText,
    currentUtterance.startTime,
    currentUtterance.endTime
  );

  // Step 2: Split the reconciled word array at the cursor position
  // Find split point by character count
  let splitWordIndex = 0;
  let charCount = 0;

  for (let i = 0; i < newEditorWords.length; i++) {
    const wordEndPos = charCount + newEditorWords[i].text.length;
    if (charCount <= cursorPosition && cursorPosition <= wordEndPos) {
      // Split occurs within or at the boundary of this word
      splitWordIndex = i;

      // If cursor is exactly at word boundary, split after this word
      if (cursorPosition === wordEndPos && i < newEditorWords.length - 1) {
        splitWordIndex = i + 1;
      }
      break;
    }
    charCount = wordEndPos + 1; // +1 for space
  }

  // Split the words array
  const beforeWords = newEditorWords.slice(0, splitWordIndex);
  const afterWords = newEditorWords.slice(splitWordIndex);

  // Update local state
  setUtterances((prev) => {
    const utteranceIndex = prev.findIndex((p) => p.id === utteranceId);
    if (utteranceIndex === -1) return prev;

    const newUtterances = [...prev];

    // Calculate new times based on the words
    const firstPartEndTime =
      beforeWords.length > 0
        ? beforeWords[beforeWords.length - 1].endTime
        : currentUtterance.startTime;

    const secondPartStartTime =
      afterWords.length > 0
        ? afterWords[0].startTime
        : currentUtterance.endTime;

    const secondPartEndTime =
      afterWords.length > 0
        ? afterWords[afterWords.length - 1].endTime
        : currentUtterance.endTime;

    newUtterances[utteranceIndex] = {
      ...newUtterances[utteranceIndex],
      id: firstPartId,
      transcriptWords: beforeWords,
      endTime: firstPartEndTime,
    };

    newUtterances.splice(utteranceIndex + 1, 0, {
      ...currentUtterance, // Preserve meta information
      id: secondPartId,
      transcriptWords: afterWords,
      startTime: secondPartStartTime,
      endTime: secondPartEndTime,
    });

    return newUtterances;
  });

  // Notify parent component
  if (callbacks.onUtteranceSplit) {
    callbacks.onUtteranceSplit(
      utteranceId,
      beforeWords,
      secondPartId,
      afterWords
    );
  }

  // Set cursor position in new utterance
  setTimeout(() => {
    const newUtteranceElement = document.querySelector(
      `[data-utterance-id="${secondPartId}"] .utterance-content`
    ) as HTMLElement;
    if (newUtteranceElement) {
      newUtteranceElement.focus();
      // Find the first text node in the new utterance
      const walker = document.createTreeWalker(
        newUtteranceElement,
        NodeFilter.SHOW_TEXT,
        null
      );
      const firstTextNode = walker.nextNode();
      if (firstTextNode) {
        const newRange = document.createRange();
        const newSelection = window.getSelection();
        if (newSelection) {
          try {
            newRange.setStart(firstTextNode, 0);
            newRange.collapse(true);
            newSelection.removeAllRanges();
            newSelection.addRange(newRange);
          } catch (rangeError) {
            console.warn('Failed to set cursor in new utterance:', rangeError);
            // Fallback: just focus the new utterance
            newUtteranceElement.focus();
          }
        }
      } else {
        // No text node found, just focus the utterance
        newUtteranceElement.focus();
      }
    }
  }, 0);
};

/**
 * Handles Backspace key at the beginning of utterance to merge with previous
 */
const handleBackspaceKey = (
  e: React.KeyboardEvent,
  utteranceId: string,
  utteranceDataMap: Map<string, { utteranceIndex: number; startTime: number }>,
  utterances: TranscriptUtterance[],
  setUtterances: React.Dispatch<React.SetStateAction<TranscriptUtterance[]>>,
  callbacks: InputHandlerCallbacks
) => {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return;

  const range = selection.getRangeAt(0);

  if (range.startOffset === 0 && range.endOffset === 0) {
    e.preventDefault();

    const utteranceData = utteranceDataMap.get(utteranceId);
    if (!utteranceData) return;

    const utteranceIndex = utteranceData.utteranceIndex;

    const currentUtterance = utterances[utteranceIndex];
    const previousUtterance = utterances[utteranceIndex - 1];
    const previousText = previousUtterance.transcriptWords
      .map((w) => w.text)
      .join(' ');
    const cursorPosition = previousText.length;

    // Update local state
    setUtterances((prev) => {
      const newUtterances = [...prev];

      const mergedWords = [
        ...previousUtterance.transcriptWords,
        ...currentUtterance.transcriptWords,
      ];

      // Calculate the new end time from the last word
      const newEndTime =
        mergedWords.length > 0
          ? mergedWords[mergedWords.length - 1].endTime
          : previousUtterance.endTime;

      newUtterances[utteranceIndex - 1] = {
        ...previousUtterance,
        transcriptWords: mergedWords,
        endTime: newEndTime,
      };
      newUtterances.splice(utteranceIndex, 1);
      return newUtterances;
    });

    // Notify parent component
    if (callbacks.onUtteranceMerge) {
      callbacks.onUtteranceMerge(utteranceId, previousUtterance.id);
    }

    setTimeout(() => {
      const mergedElement = document.querySelector(
        `[data-utterance-id="${previousUtterance.id}"] .utterance-content`
      ) as HTMLElement;
      if (mergedElement) {
        // Find the correct text node and position within the merged utterance
        const walker = document.createTreeWalker(
          mergedElement,
          NodeFilter.SHOW_TEXT,
          null
        );

        let currentOffset = 0;
        let textNode = walker.nextNode();
        let targetFound = false;

        while (textNode && !targetFound) {
          const nodeLength = textNode.textContent?.length || 0;

          if (currentOffset + nodeLength >= cursorPosition) {
            // Found the target node
            const newRange = document.createRange();
            const newSelection = window.getSelection();
            if (newSelection) {
              try {
                const offsetInNode = cursorPosition - currentOffset;
                const safeOffset = Math.max(
                  0,
                  Math.min(offsetInNode, nodeLength)
                );
                newRange.setStart(textNode, safeOffset);
                newRange.collapse(true);
                newSelection.removeAllRanges();
                newSelection.addRange(newRange);
                targetFound = true;
              } catch (rangeError) {
                console.warn(
                  'Failed to set cursor in merged utterance:',
                  rangeError
                );
                // Continue to fallback
              }
            }
          }

          if (!targetFound) {
            currentOffset += nodeLength;
            textNode = walker.nextNode();
          }
        }

        // Fallback: just focus the merged element
        if (!targetFound) {
          mergedElement.focus();
        }
      }
    }, 0);
  }
};

/**
 * Cycles the confidence of a word at the specified index in an utterance
 */
export const cycleWordConfidenceAtIndex = (
  utterance: TranscriptUtterance,
  wordIndex: number,
  callbacks: InputHandlerCallbacks
): void => {
  // Check if wordIndex is valid
  if (wordIndex < 0 || wordIndex >= utterance.transcriptWords.length) return;

  const word = utterance.transcriptWords[wordIndex];
  const updatedWord = cycleWordConfidence(word);

  // Create new words array with the updated word
  const newEditorWords = [...utterance.transcriptWords];
  newEditorWords[wordIndex] = updatedWord;

  // Notify parent component about the word changes
  if (callbacks.onWordsEdit) {
    callbacks.onWordsEdit(utterance.id, newEditorWords);
  }
};

/**
 * Main keyboard event handler that delegates to specific key handlers
 */
export const handleKeyDown = (
  e: React.KeyboardEvent,
  utteranceId: string,
  utteranceDataMap: Map<string, { utteranceIndex: number; startTime: number }>,
  utterances: TranscriptUtterance[],
  setUtterances: React.Dispatch<React.SetStateAction<TranscriptUtterance[]>>,
  callbacks: InputHandlerCallbacks
) => {
  if (e.key === 'Enter') {
    handleEnterKey(
      e,
      utteranceId,
      utteranceDataMap,
      utterances,
      setUtterances,
      callbacks
    );
  } else if (e.key === 'Backspace') {
    handleBackspaceKey(
      e,
      utteranceId,
      utteranceDataMap,
      utterances,
      setUtterances,
      callbacks
    );
  }
};
